import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Backdrop,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Checklist as ChecklistIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Close as CloseIcon,
    Download as DownloadIcon,
    ImageNotSupported as NoPhotoIcon,
    LocalShipping as LocalShippingIcon,
    PhotoLibrary as PhotoLibraryIcon,
    PictureAsPdf as PictureAsPdfIcon,
    Refresh as RefreshIcon,
    RvHookup as RvHookupIcon,
    TripOrigin as TireIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import {
    PreTripInspectionItemResponseDto,
    useApprovePtiMutation,
    useRejectPtiMutation,
    useGetPtiByIdQuery,
    useRefreshPtiAssetMutation,
    useUpdatePtiMeasurementsMutation,
} from "../../api/ptiApi";
import { useAppSelector } from "../../app/hooks";
import { useToast } from "../../contexts/ToastContext";
import PageLayout from "../../components/layout/PageLayout";
import { getPtiAssetDisplayLabel, getPtiPositionLabel, getPtiStatusLabel, PTI_STATUS } from "./ptiConstants";
import { mergePtiReviewItems } from "./ptiPositions";
import { getDamageZonesForPosition, getPtiChecklistQuestions } from "./ptiChecklist";
import {
    formatTreadDepth,
    getBrakeMeasurementZone,
    getBrakeScaleHint,
    getBrakeZoneHelperText,
    getChipColor,
    getMeasurementTextFieldSx,
    getTreadDepthZone,
    getTreadScaleHint,
    getTreadZoneHelperText,
} from "./ptiMeasurementZones";
import PtiMiniDiagram from "./components/PtiMiniDiagram";
import PtiChecklistPanel from "./components/PtiChecklistPanel";
import PtiDocumentPreview from "./components/PtiDocumentPreview";
import { PtiMeasurementInfoField } from "./components/PtiMeasurementInfoField";
import { downloadElementAsPdf } from "./exportPtiDocumentPdf";
import {
    getFluidLevelChipColor,
    getFluidLevelLabel,
    isFluidLevelPosition,
    PTI_FLUID_LEVEL_OPTIONS,
} from "./ptiFluidLevels";
import {
    getPtiApiErrorMessage,
    getPtiItemKey,
    hasPtiItemImage,
    type PtiMeasurementDraft,
} from "./ptiReviewUtils";

const PTI_DOCUMENT_EXPORT_ROOT_ID = "pti-document-export-root";

type ReviewFilterTab = "exterior" | "tires";
type ReviewSectionTab = "photos" | "checklist";

const PtiReviewPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const inspectionId = Number(id);
    const hasValidId = Number.isFinite(inspectionId) && inspectionId > 0;

    const thumbStripRef = useRef<HTMLDivElement | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [measurementDrafts, setMeasurementDrafts] = useState<Record<string, PtiMeasurementDraft>>({});
    const [rejectReason, setRejectReason] = useState("");
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [isApprovingFlow, setIsApprovingFlow] = useState(false);
    const [reviewFilterTab, setReviewFilterTab] = useState<ReviewFilterTab>("exterior");
    const [sectionTab, setSectionTab] = useState<ReviewSectionTab>("photos");
    const [isDocumentPreviewOpen, setIsDocumentPreviewOpen] = useState(false);
    const [isPdfBusy, setIsPdfBusy] = useState(false);
    const isMountedRef = useRef(true);
    const { showSuccess, showError, showLoading } = useToast();
    const token = useAppSelector((state) => state.auth.token);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const { data: inspectionData, isLoading, isFetching } = useGetPtiByIdQuery(inspectionId, {
        skip: !hasValidId,
    });
    const inspection = inspectionData?.id === inspectionId ? inspectionData : undefined;
    const isInspectionLoading = hasValidId && !inspection && (isLoading || isFetching);

    const [updateMeasurements, { isLoading: isSaving }] = useUpdatePtiMeasurementsMutation();
    const [approvePti] = useApprovePtiMutation();
    const [rejectPti, { isLoading: isRejecting }] = useRejectPtiMutation();
    const [refreshAsset, { isLoading: isRefreshingAsset }] = useRefreshPtiAssetMutation();

    const items = useMemo(
        () => (inspection ? mergePtiReviewItems(inspection) : []),
        [inspection]
    );
    const photoZones = useMemo(
        () =>
            items
                .filter((item) => hasPtiItemImage(item))
                .map((item) => ({
                    positionKey: item.positionKey,
                    itemType: item.itemType,
                })),
        [items]
    );
    const checklistEntries = useMemo(() => {
        if (!inspection) {
            return [];
        }

        const questions = getPtiChecklistQuestions(inspection.assetType);
        return questions.filter(
            (question) => inspection.checklistAnswers?.[question.key] !== undefined
        );
    }, [inspection]);
    const exteriorItems = useMemo(
        () => items.filter((item) => item.itemType === "BodyPhoto"),
        [items]
    );
    const tireItems = useMemo(
        () => items.filter((item) => item.itemType === "Tire"),
        [items]
    );
    const filteredItems = useMemo(() => {
        if (reviewFilterTab === "tires") {
            return tireItems;
        }
        return exteriorItems;
    }, [reviewFilterTab, exteriorItems, tireItems]);

    const isEditable = inspection?.status === PTI_STATUS.Submitted;

    const navigateBackToList = () => {
        navigate("/pti");
    };

    useEffect(() => {
        if (!inspection) {
            return;
        }

        setReviewFilterTab("exterior");
        setActiveIndex(0);
        setShowRejectForm(false);
        setRejectReason("");
        setActionError(null);

        const drafts: Record<string, PtiMeasurementDraft> = {};
        items.forEach((item) => {
            if (item.itemType === "Tire" || isFluidLevelPosition(item.positionKey)) {
                drafts[getPtiItemKey(item)] = {
                    treadDepth: item.treadDepth != null ? String(item.treadDepth) : "",
                    brakeMeasurement:
                        item.brakeMeasurement != null ? String(item.brakeMeasurement) : "",
                    tirePsi: item.tirePsi != null ? String(item.tirePsi) : "",
                    fluidLevel: item.fluidLevel ?? null,
                };
            }
        });
        setMeasurementDrafts(drafts);
    }, [inspection?.id, items, inspection]);

    useEffect(() => {
        if (!thumbStripRef.current) {
            return;
        }

        const thumb = thumbStripRef.current.querySelector<HTMLElement>(
            `[data-pti-thumb-index="${activeIndex}"]`
        );
        thumb?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }, [activeIndex, reviewFilterTab]);

    const activeItem = filteredItems[activeIndex];
    const activeDraft = activeItem ? measurementDrafts[getPtiItemKey(activeItem)] : undefined;
    const activeTreadValue = activeDraft?.treadDepth
        ? Number(activeDraft.treadDepth)
        : activeItem?.treadDepth ?? null;
    const activeBrakeValue = activeDraft?.brakeMeasurement
        ? Number(activeDraft.brakeMeasurement)
        : activeItem?.brakeMeasurement ?? null;
    const activeTreadZone = activeItem
        ? getTreadDepthZone(activeItem.positionKey, activeTreadValue)
        : "unknown";
    const activeBrakeZone = getBrakeMeasurementZone(activeBrakeValue);

    const goPrev = () => {
        if (filteredItems.length === 0) return;
        setActiveIndex((current) => (current > 0 ? current - 1 : filteredItems.length - 1));
    };

    const goNext = () => {
        if (filteredItems.length === 0) return;
        setActiveIndex((current) => (current < filteredItems.length - 1 ? current + 1 : 0));
    };

    const handleReviewFilterChange = (
        _: React.MouseEvent<HTMLElement>,
        value: ReviewFilterTab | null
    ) => {
        if (!value) {
            return;
        }
        setReviewFilterTab(value);
        setActiveIndex(0);
    };

    const handleSectionTabChange = (_: React.SyntheticEvent, value: ReviewSectionTab) => {
        setSectionTab(value);
    };

    const handleDiagramZonePress = (
        positionKey: string,
        itemType: PreTripInspectionItemResponseDto["itemType"]
    ) => {
        setSectionTab("photos");
        const nextTab: ReviewFilterTab = itemType === "Tire" ? "tires" : "exterior";
        setReviewFilterTab(nextTab);
        const pool = nextTab === "tires" ? tireItems : exteriorItems;
        const index = pool.findIndex(
            (item) => item.positionKey === positionKey && item.itemType === itemType
        );
        setActiveIndex(index >= 0 ? index : 0);
    };

    const buildMeasurementPayload = () =>
        Object.entries(measurementDrafts)
            .map(([key, draft]) => {
                const item = items.find((entry) => getPtiItemKey(entry) === key);
                if (!item) {
                    return null;
                }

                if (item.itemType === "Tire") {
                    const treadDepth = draft.treadDepth ? Number(draft.treadDepth) : null;
                    const brakeMeasurement = draft.brakeMeasurement
                        ? Number(draft.brakeMeasurement)
                        : null;
                    const tirePsi = draft.tirePsi ? Number(draft.tirePsi) : null;

                    if (treadDepth == null && brakeMeasurement == null && tirePsi == null) {
                        return null;
                    }

                    return {
                        itemType: item.itemType,
                        positionKey: item.positionKey,
                        treadDepth,
                        brakeMeasurement,
                        tirePsi,
                    };
                }

                if (isFluidLevelPosition(item.positionKey)) {
                    if (!draft.fluidLevel) {
                        return null;
                    }

                    return {
                        itemType: item.itemType,
                        positionKey: item.positionKey,
                        fluidLevel: draft.fluidLevel,
                    };
                }

                return null;
            })
            .filter(Boolean);

    const handleSaveMeasurements = async () => {
        if (!inspection) {
            return;
        }

        setActionError(null);
        try {
            await updateMeasurements({
                inspectionId: inspection.id,
                body: { items: buildMeasurementPayload() as any },
            }).unwrap();
            showSuccess("Measurements saved.");
        } catch (error: any) {
            const message = getPtiApiErrorMessage(error, "Failed to save measurements.");
            setActionError(message);
            showError(message);
        }
    };

    const handleApprove = async () => {
        if (!inspection) {
            return;
        }

        setActionError(null);
        setIsApprovingFlow(true);
        try {
            const payload = buildMeasurementPayload();
            if (payload.length > 0) {
                await updateMeasurements({
                    inspectionId: inspection.id,
                    body: { items: payload as any },
                }).unwrap();
            }

            await approvePti(inspection.id).unwrap();
            showSuccess("PTI approved successfully.");
            navigateBackToList();
        } catch (error: any) {
            const message = getPtiApiErrorMessage(error, "Failed to approve PTI.");
            setActionError(message);
            showError(message);
        } finally {
            setIsApprovingFlow(false);
        }
    };

    const handleReject = async () => {
        if (!inspection || !rejectReason.trim()) {
            return;
        }

        setActionError(null);
        try {
            await rejectPti({
                inspectionId: inspection.id,
                body: { reason: rejectReason.trim() },
            }).unwrap();
            showSuccess("PTI rejected.");
            navigateBackToList();
        } catch (error: any) {
            const message = getPtiApiErrorMessage(error, "Failed to reject PTI.");
            setActionError(message);
            showError(message);
        }
    };

    const handleRefreshAsset = async () => {
        if (!inspection) {
            return;
        }

        setActionError(null);
        try {
            await refreshAsset(inspection.id).unwrap();
            showSuccess("Asset details refreshed.");
        } catch (error: any) {
            const message = getPtiApiErrorMessage(error, "Failed to refresh asset details.");
            setActionError(message);
            showError(message);
        }
    };

    const handlePreviewDocument = () => {
        if (!inspection) {
            return;
        }
        setIsDocumentPreviewOpen(true);
    };

    const handleDownloadPdf = async () => {
        if (!inspection) {
            return;
        }

        if (!token) {
            showError("You must be signed in to download the PDF.");
            return;
        }

        const exportRoot = document.getElementById(PTI_DOCUMENT_EXPORT_ROOT_ID);
        if (!exportRoot) {
            showError("Document preview is not ready yet. Try again in a moment.");
            return;
        }

        if (isPdfBusy) {
            return;
        }

        setIsPdfBusy(true);
        setActionError(null);
        showLoading("Downloading PTI report", "Preparing document…");

        try {
            const unit = getPtiAssetDisplayLabel(inspection).replace(/[^\w.-]+/g, "_");
            await downloadElementAsPdf({
                element: exportRoot,
                fileName: `PTI-${inspection.id}-${unit}`,
                inspectionId: inspection.id,
                token,
                onProgress: (progress) =>
                    showLoading("Downloading PTI report", progress.message),
            });
            showSuccess("PTI PDF downloaded.");
        } catch (error: any) {
            const message = error?.message || "Failed to download PTI PDF.";
            if (isMountedRef.current) {
                setActionError(message);
            }
            showError(message);
        } finally {
            if (isMountedRef.current) {
                setIsPdfBusy(false);
            }
        }
    };

    const renderSelectedDetails = () => {
        if (!activeItem) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No inspection items available.
                </Typography>
            );
        }

        return (
            <>
                <Typography variant="h6">
                    {getPtiPositionLabel(activeItem.positionKey)}
                </Typography>

                {activeItem.note ? (
                    <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            Note
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {activeItem.note}
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        No note for this position.
                    </Typography>
                )}

                {!!activeItem.damageMarks?.length && (
                    <Box sx={{ mt: 1.5 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            display="block"
                        >
                            Damage marks
                        </Typography>
                        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                            {activeItem.damageMarks!.map((mark) => {
                                const zoneLabel =
                                    getDamageZonesForPosition(activeItem.positionKey).find(
                                        (zone) => zone.zoneKey === mark.zoneKey
                                    )?.label ?? mark.zoneKey;
                                return (
                                    <Box
                                        key={`${mark.zoneKey}-${mark.zoneNumber}`}
                                        sx={{
                                            px: 1,
                                            py: 0.75,
                                            borderRadius: 1,
                                            bgcolor: "action.hover",
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={600}>
                                            #{mark.zoneNumber} {zoneLabel}
                                        </Typography>
                                        {mark.note?.trim() ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {mark.note}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                )}

                {activeItem.itemType === "Tire" && isEditable && activeDraft && (
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        sx={{ mt: 2 }}
                    >
                        <PtiMeasurementInfoField
                            label="Tread (/32)"
                            infoText={getTreadScaleHint(activeItem.positionKey)}
                            helperText={getTreadZoneHelperText(
                                activeItem.positionKey,
                                activeTreadZone
                            )}
                            value={activeDraft.treadDepth}
                            fieldSx={getMeasurementTextFieldSx(activeTreadZone)}
                            onChange={(treadDepth) =>
                                setMeasurementDrafts((current) => ({
                                    ...current,
                                    [getPtiItemKey(activeItem)]: {
                                        ...current[getPtiItemKey(activeItem)],
                                        treadDepth,
                                    },
                                }))
                            }
                        />
                        <PtiMeasurementInfoField
                            label="Brake (mm)"
                            infoText={getBrakeScaleHint()}
                            helperText={getBrakeZoneHelperText(activeBrakeZone)}
                            value={activeDraft.brakeMeasurement}
                            fieldSx={getMeasurementTextFieldSx(activeBrakeZone)}
                            onChange={(brakeMeasurement) =>
                                setMeasurementDrafts((current) => ({
                                    ...current,
                                    [getPtiItemKey(activeItem)]: {
                                        ...current[getPtiItemKey(activeItem)],
                                        brakeMeasurement,
                                    },
                                }))
                            }
                        />
                        <PtiMeasurementInfoField
                            label="Tire PSI"
                            infoText="Cold tire pressure in PSI as recorded during the inspection."
                            helperText="Optional"
                            value={activeDraft.tirePsi}
                            onChange={(tirePsi) =>
                                setMeasurementDrafts((current) => ({
                                    ...current,
                                    [getPtiItemKey(activeItem)]: {
                                        ...current[getPtiItemKey(activeItem)],
                                        tirePsi,
                                    },
                                }))
                            }
                        />
                    </Stack>
                )}

                {activeItem.itemType === "Tire" && !isEditable && (
                    <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
                        {activeItem.treadDepth != null && (
                            <Chip
                                size="small"
                                color={getChipColor(activeTreadZone)}
                                label={`Tread: ${formatTreadDepth(activeItem.treadDepth)}`}
                            />
                        )}
                        {activeItem.brakeMeasurement != null && (
                            <Chip
                                size="small"
                                color={getChipColor(activeBrakeZone)}
                                label={`Brake: ${activeItem.brakeMeasurement}mm`}
                            />
                        )}
                        {activeItem.tirePsi != null && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`PSI: ${activeItem.tirePsi}`}
                            />
                        )}
                    </Stack>
                )}

                {isFluidLevelPosition(activeItem.positionKey) && (
                    <Box sx={{ mt: 2 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            display="block"
                            sx={{ mb: 0.75 }}
                        >
                            Fluid level
                        </Typography>
                        {isEditable ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {PTI_FLUID_LEVEL_OPTIONS.map((option) => {
                                    const selected = activeDraft?.fluidLevel === option.value;
                                    return (
                                        <Chip
                                            key={option.value}
                                            size="small"
                                            label={option.label}
                                            color={
                                                selected
                                                    ? getFluidLevelChipColor(option.value)
                                                    : "default"
                                            }
                                            variant={selected ? "filled" : "outlined"}
                                            onClick={() => {
                                                const key = getPtiItemKey(activeItem);
                                                setMeasurementDrafts((current) => {
                                                    const existing = current[key] ?? {
                                                        treadDepth: "",
                                                        brakeMeasurement: "",
                                                        tirePsi: "",
                                                    };
                                                    return {
                                                        ...current,
                                                        [key]: {
                                                            ...existing,
                                                            fluidLevel: option.value,
                                                        },
                                                    };
                                                });
                                            }}
                                            sx={{ cursor: "pointer" }}
                                        />
                                    );
                                })}
                            </Stack>
                        ) : activeItem.fluidLevel ? (
                            <Chip
                                size="small"
                                color={getFluidLevelChipColor(activeItem.fluidLevel)}
                                label={getFluidLevelLabel(activeItem.fluidLevel)}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No fluid level recorded.
                            </Typography>
                        )}
                    </Box>
                )}
            </>
        );
    };

    return (
        <PageLayout
            variant="fullHeight"
            sx={{
                height: "100%",
                maxHeight: "100%",
                py: 1.5,
                pb: 0,
                display: "flex",
                flexDirection: "column",
                gap: 0,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    pb: 1.5,
                }}
            >
                <Breadcrumbs sx={{ mb: 1.25, flexShrink: 0 }} aria-label="PTI breadcrumbs">
                    <Link
                        component={RouterLink}
                        to="/pti"
                        underline="hover"
                        color="text.secondary"
                        variant="body2"
                    >
                        Pre-Trip Inspections
                    </Link>
                    <Typography variant="body2" color="text.primary" fontWeight={700}>
                        {inspection
                            ? getPtiAssetDisplayLabel(inspection)
                            : "Review"}
                    </Typography>
                </Breadcrumbs>

                {!hasValidId ? (
                    <Alert severity="error">Invalid inspection id.</Alert>
                ) : isInspectionLoading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress />
                    </Box>
                ) : !inspection ? (
                    <Alert severity="warning">PTI not found.</Alert>
                ) : (
                    <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                        {actionError && (
                            <Alert
                                severity="error"
                                onClose={() => setActionError(null)}
                                sx={{ flexShrink: 0 }}
                            >
                                {actionError}
                            </Alert>
                        )}

                        <Paper variant="outlined" sx={{ px: 1.75, py: 1.25, flexShrink: 0 }}>
                            <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="flex-start"
                                justifyContent="space-between"
                            >
                                <Stack
                                    direction="row"
                                    spacing={1.25}
                                    alignItems="center"
                                    sx={{ minWidth: 0, flex: 1 }}
                                >
                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: (theme) =>
                                                `${theme.palette.primary.main}14`,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {inspection.assetType === "Vehicle" ? (
                                            <LocalShippingIcon color="primary" fontSize="small" />
                                        ) : (
                                            <RvHookupIcon color="primary" fontSize="small" />
                                        )}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="h6"
                                            color="text.primary"
                                            fontWeight={700}
                                            noWrap
                                            sx={{ lineHeight: 1.25 }}
                                        >
                                            {getPtiAssetDisplayLabel(inspection)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {format(
                                                new Date(inspection.inspectionDate),
                                                "MMM d, yyyy h:mm a"
                                            )}
                                            {" · "}
                                            {inspection.submittedByName || "Unknown user"}
                                            {inspection.odometer != null
                                                ? ` · ${inspection.odometer.toLocaleString()} mi`
                                                : ""}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                    flexShrink={0}
                                    sx={{ flexWrap: "wrap", justifyContent: "flex-end", gap: 0.75 }}
                                    useFlexGap
                                >
                                    <Tooltip title="Refresh asset name/unit from the current vehicle or trailer record">
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label="Refresh asset details"
                                                disabled={isRefreshingAsset}
                                                onClick={handleRefreshAsset}
                                            >
                                                <RefreshIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Preview document">
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label="Preview document"
                                                onClick={handlePreviewDocument}
                                            >
                                                <PictureAsPdfIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Download PDF">
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label="Download PDF"
                                                disabled={isPdfBusy}
                                                onClick={handleDownloadPdf}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Chip
                                        label={getPtiStatusLabel(inspection.status)}
                                        color={
                                            inspection.status === PTI_STATUS.Completed
                                                ? "success"
                                                : inspection.status === PTI_STATUS.Rejected
                                                  ? "error"
                                                  : "warning"
                                        }
                                        sx={{
                                            height: 32,
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                            px: 0.5,
                                        }}
                                    />
                                </Stack>
                            </Stack>

                            {inspection.rejectionReason && (
                                <Alert severity="warning" sx={{ mt: 1.25 }}>
                                    Rejection reason: {inspection.rejectionReason}
                                </Alert>
                            )}

                            {inspection.notes && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 1, display: "block" }}
                                >
                                    Note: {inspection.notes}
                                </Typography>
                            )}
                        </Paper>

                        <Paper
                            variant="outlined"
                            sx={{
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                                minHeight: 0,
                            }}
                        >
                            <Tabs
                                value={sectionTab}
                                onChange={handleSectionTabChange}
                                sx={{
                                    minHeight: 44,
                                    px: 1,
                                    flexShrink: 0,
                                    borderBottom: 1,
                                    borderColor: "divider",
                                    "& .MuiTab-root": {
                                        minHeight: 44,
                                        py: 0.75,
                                        textTransform: "none",
                                        fontSize: 13,
                                        fontWeight: 600,
                                    },
                                }}
                            >
                                <Tab
                                    icon={<PhotoLibraryIcon fontSize="small" />}
                                    iconPosition="start"
                                    label="Photos"
                                    value="photos"
                                />
                                <Tab
                                    icon={<ChecklistIcon fontSize="small" />}
                                    iconPosition="start"
                                    label={`Checklist${
                                        checklistEntries.length
                                            ? ` (${checklistEntries.length})`
                                            : ""
                                    }`}
                                    value="checklist"
                                    disabled={checklistEntries.length === 0}
                                />
                            </Tabs>

                            {sectionTab === "photos" ? (
                                <Box
                                    sx={{
                                        p: 1.5,
                                        flex: 1,
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                lg: "minmax(0, 1.55fr) minmax(260px, 0.85fr)",
                                            },
                                            gap: 1.5,
                                            flex: 1,
                                            minHeight: {
                                                xs: 360,
                                                md: 480,
                                            },
                                            height: { lg: "100%" },
                                            alignItems: "stretch",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: 0,
                                                minHeight: 0,
                                                height: { lg: "100%" },
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    flex: 1,
                                                    minHeight: { xs: 260, md: 320 },
                                                    bgcolor: "grey.900",
                                                    borderRadius: 1.5,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                {activeItem && hasPtiItemImage(activeItem) ? (
                                                    <Box
                                                        component="img"
                                                        src={activeItem.url!}
                                                        alt={getPtiPositionLabel(
                                                            activeItem.positionKey
                                                        )}
                                                        sx={{
                                                            position: "absolute",
                                                            inset: 0,
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "contain",
                                                            display: "block",
                                                        }}
                                                    />
                                                ) : (
                                                    <Stack
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        spacing={1}
                                                        sx={{ position: "absolute", inset: 0 }}
                                                    >
                                                        <NoPhotoIcon
                                                            sx={{ fontSize: 48, color: "grey.600" }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            color="grey.500"
                                                        >
                                                            No photo captured
                                                        </Typography>
                                                    </Stack>
                                                )}

                                                <ToggleButtonGroup
                                                    exclusive
                                                    size="small"
                                                    value={reviewFilterTab}
                                                    onChange={handleReviewFilterChange}
                                                    sx={{
                                                        position: "absolute",
                                                        top: 8,
                                                        right: 8,
                                                        zIndex: 2,
                                                        bgcolor: "rgba(0,0,0,0.55)",
                                                        backdropFilter: "blur(4px)",
                                                        borderRadius: 1,
                                                        "& .MuiToggleButton-root": {
                                                            color: "grey.300",
                                                            border: 0,
                                                            px: 1,
                                                            py: 0.5,
                                                            textTransform: "none",
                                                            gap: 0.5,
                                                            fontSize: 12,
                                                            "&.Mui-selected": {
                                                                color: "common.white",
                                                                bgcolor: "primary.main",
                                                                "&:hover": {
                                                                    bgcolor: "primary.dark",
                                                                },
                                                            },
                                                            "&:hover": {
                                                                bgcolor: "rgba(255,255,255,0.08)",
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <ToggleButton value="exterior" aria-label="Exterior">
                                                        <LocalShippingIcon sx={{ fontSize: 16 }} />
                                                        Exterior
                                                    </ToggleButton>
                                                    <ToggleButton value="tires" aria-label="Tires">
                                                        <TireIcon sx={{ fontSize: 16 }} />
                                                        Tires
                                                    </ToggleButton>
                                                </ToggleButtonGroup>

                                                {filteredItems.length > 1 && (
                                                    <>
                                                        <IconButton
                                                            aria-label="Previous photo"
                                                            onClick={goPrev}
                                                            sx={{
                                                                position: "absolute",
                                                                left: 8,
                                                                top: "50%",
                                                                transform: "translateY(-50%)",
                                                                bgcolor: "rgba(0,0,0,0.45)",
                                                                color: "common.white",
                                                                "&:hover": {
                                                                    bgcolor: "rgba(0,0,0,0.65)",
                                                                },
                                                            }}
                                                        >
                                                            <ChevronLeftIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            aria-label="Next photo"
                                                            onClick={goNext}
                                                            sx={{
                                                                position: "absolute",
                                                                right: 8,
                                                                top: "50%",
                                                                transform: "translateY(-50%)",
                                                                bgcolor: "rgba(0,0,0,0.45)",
                                                                color: "common.white",
                                                                "&:hover": {
                                                                    bgcolor: "rgba(0,0,0,0.65)",
                                                                },
                                                            }}
                                                        >
                                                            <ChevronRightIcon />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>

                                            <Box
                                                ref={thumbStripRef}
                                                sx={{
                                                    display: "flex",
                                                    gap: 0.75,
                                                    overflowX: "auto",
                                                    flexShrink: 0,
                                                    pb: 0.25,
                                                }}
                                            >
                                                {filteredItems.map((item, index) => (
                                                    <Box
                                                        key={getPtiItemKey(item)}
                                                        data-pti-thumb-index={index}
                                                        onClick={() => setActiveIndex(index)}
                                                        sx={{
                                                            width: 64,
                                                            height: 64,
                                                            flexShrink: 0,
                                                            borderRadius: 1,
                                                            overflow: "hidden",
                                                            border: 2,
                                                            borderColor:
                                                                index === activeIndex
                                                                    ? "primary.main"
                                                                    : "divider",
                                                            cursor: "pointer",
                                                            bgcolor: "action.hover",
                                                            opacity: hasPtiItemImage(item) ? 1 : 0.55,
                                                        }}
                                                    >
                                                        {hasPtiItemImage(item) ? (
                                                            <Box
                                                                component="img"
                                                                src={item.url!}
                                                                alt={getPtiPositionLabel(
                                                                    item.positionKey
                                                                )}
                                                                sx={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "cover",
                                                                }}
                                                            />
                                                        ) : (
                                                            <Stack
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                sx={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                }}
                                                            >
                                                                <NoPhotoIcon
                                                                    sx={{
                                                                        fontSize: 20,
                                                                        color: "text.disabled",
                                                                    }}
                                                                />
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>

                                        <Stack
                                            spacing={1.5}
                                            sx={{
                                                minWidth: 0,
                                                minHeight: 0,
                                                height: "100%",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    border: 1,
                                                    borderColor: "divider",
                                                    borderRadius: 1.5,
                                                    p: 1.5,
                                                    bgcolor: "background.paper",
                                                    minWidth: 0,
                                                    minHeight: 0,
                                                    flex: 1,
                                                    overflow: "auto",
                                                }}
                                            >
                                                {renderSelectedDetails()}
                                            </Box>

                                            <Box
                                                sx={{
                                                    border: 1,
                                                    borderColor: "divider",
                                                    borderRadius: 1.5,
                                                    p: 1.5,
                                                    bgcolor: "action.hover",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ mb: 1, alignSelf: "flex-start" }}
                                                >
                                                    Click a zone to jump to its photo
                                                </Typography>
                                                {activeItem && (
                                                    <PtiMiniDiagram
                                                        assetType={inspection.assetType}
                                                        highlightPositionKey={
                                                            activeItem.positionKey
                                                        }
                                                        highlightItemType={activeItem.itemType}
                                                        width={160}
                                                        onZonePress={handleDiagramZonePress}
                                                        photoZones={photoZones}
                                                    />
                                                )}
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ p: 1.5, flex: 1, minHeight: 0, overflow: "auto" }}>
                                    <PtiChecklistPanel
                                        questions={checklistEntries}
                                        answers={inspection.checklistAnswers ?? {}}
                                    />
                                </Box>
                            )}
                        </Paper>
                    </Stack>
                )}
            </Box>

            {inspection && isEditable && (
                <Paper
                    elevation={0}
                    square
                    sx={{
                        flexShrink: 0,
                        position: "sticky",
                        bottom: 0,
                        mx: -3,
                        px: 3,
                        py: 1.25,
                        borderTop: 1,
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        zIndex: (theme) => theme.zIndex.appBar,
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ sm: "center" }}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        {showRejectForm ? (
                            <>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Rejection reason"
                                    value={rejectReason}
                                    onChange={(event) => setRejectReason(event.target.value)}
                                    sx={{ flex: 1, minWidth: { sm: 240 } }}
                                />
                                <Button onClick={() => setShowRejectForm(false)}>Cancel</Button>
                                <Button
                                    color="error"
                                    variant="contained"
                                    disabled={!rejectReason.trim() || isRejecting}
                                    onClick={handleReject}
                                >
                                    Confirm Reject
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button color="error" onClick={() => setShowRejectForm(true)}>
                                    Reject
                                </Button>
                                <Box sx={{ flex: 1 }} />
                                <Button
                                    variant="outlined"
                                    disabled={isSaving || isApprovingFlow}
                                    onClick={handleSaveMeasurements}
                                >
                                    Save Measurements
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={isApprovingFlow || isSaving}
                                    onClick={handleApprove}
                                >
                                    Approve
                                </Button>
                            </>
                        )}
                    </Stack>
                </Paper>
            )}

            <Backdrop
                open={isApprovingFlow}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: "#fff" }}
            >
                <CircularProgress color="inherit" size={48} />
            </Backdrop>

            <Dialog
                open={isDocumentPreviewOpen}
                onClose={() => setIsDocumentPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                    }}
                >
                    Inspection document preview
                    <IconButton
                        aria-label="Close document preview"
                        onClick={() => setIsDocumentPreviewOpen(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: "grey.200", maxHeight: "80vh" }}>
                    {inspection && <PtiDocumentPreview inspection={inspection} />}
                </DialogContent>
                <DialogActions>
                    <Button
                        startIcon={<DownloadIcon />}
                        disabled={isPdfBusy}
                        onClick={handleDownloadPdf}
                    >
                        Download PDF
                    </Button>
                    <Button onClick={() => setIsDocumentPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {inspection && (
                <Box
                    aria-hidden
                    sx={{
                        // Keep fully opaque + visible for html2canvas; park off-screen only.
                        // visibility:hidden / opacity:0 produce blank PDF pages.
                        position: "fixed",
                        left: -10000,
                        top: 0,
                        width: 816,
                        pointerEvents: "none",
                        zIndex: -1,
                    }}
                >
                    <PtiDocumentPreview
                        inspection={inspection}
                        exportRootId={PTI_DOCUMENT_EXPORT_ROOT_ID}
                    />
                </Box>
            )}
        </PageLayout>
    )
};

export default PtiReviewPage;
